import axios from "axios";

const VEHICLE_SERVICE_URL =
  import.meta.env.VITE_VEHICLE_API_URL || "http://localhost:8000/api/vehicles";
// Use API Gateway for rider-related calls if available, otherwise fall back to direct rider service
const RIDER_SERVICE_URL =
  import.meta.env.VITE_API_GATEWAY_URL
    ? `${import.meta.env.VITE_API_GATEWAY_URL}/api`
    : import.meta.env.VITE_RIDER_API_URL || "http://localhost:3000/api";

const API_GATEWAY_BASE = (() => {
  const configured = import.meta.env.VITE_API_GATEWAY_URL || import.meta.env.VITE_API_URL || `${window.location.origin}/api`;
  const normalized = configured.replace(/\/+$/, "");
  return normalized.endsWith("/api") ? normalized : `${normalized}/api`;
})();

// Configure axios instance for vehicle service
const vehicleApi = axios.create({
  baseURL: VEHICLE_SERVICE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Configure axios instance for rider service
const riderApi = axios.create({
  baseURL: RIDER_SERVICE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token (for both APIs)
const addAuthToken = (config: any) => {
  const token = localStorage.getItem("authToken");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
};

vehicleApi.interceptors.request.use(addAuthToken);
riderApi.interceptors.request.use(addAuthToken);

// Remove Content-Type header for FormData so the browser/axios can set the multipart boundary
const removeFormDataContentType = (config: any) => {
  try {
    if (config && config.data instanceof FormData) {
      // Let the browser set Content-Type with boundary
      if (config.headers) delete config.headers["Content-Type"];
    }
  } catch (err) {
    // ignore
  }
  return config;
};

vehicleApi.interceptors.request.use(removeFormDataContentType);
riderApi.interceptors.request.use(removeFormDataContentType);

export interface RiderVehicleHistory {
  vehicleAgeDays: string;
  clientName?: string;
  id: string;
  riderId: string;
  vehicleId: string;
  assignedAt: string;
  returnedAt: string | null;
  assignedBy: string;
  returnedBy: string | null;
  dailyRent?: number | null;
  registrationNumber: string;
  vehicleMake?: string;
  vehicleModel?: string;
  status: "ACTIVE" | "RETURNED";
  notes: string | null;
  durationDays?: number; // Calculated field
  startMileage?: number;
  endMileage?: number;
  AssigningOdometerCount?: number;
  ReturningOdometerCount?: number;
  batteryPercentageStart?: number;
  batteryPercentageEnd?: number;
  conditionOnAssign?: string;
  conditionOnReturn?: string;
  damagesReported?: string;
  riderFeedback?: string;
  issuesReported?: string;
  hubId?: string; // Hub ID
  hubCode?: string; // Hub code (e.g., "HUB001")
  hubName?: string; // Hub name
  assignmentImages?: {
    front?: string | null;
    back?: string | null;
    left?: string | null;
    right?: string | null;
    odometer?: string | null;
    virtual?: string | null;
    charger?: string | null;
    battery?: string | null;
  };
  unassignmentImages?: {
    front?: string | null;
    back?: string | null;
    left?: string | null;
    right?: string | null;
    odometer?: string | null;
    virtual?: string | null;
    charger?: string | null;
    battery?: string | null;
  };
  vehicle?: any; // This can be a string (JSON) or an object from backend
}

export interface CurrentVehicleStatusAnalyticsRow {
  city: string;
  vehicleNumber: string;
  ev91RiderId: string;
  clientRiderId: string;
  riderName: string;
  riderContact: string;
  currentStatus: "Deployed" | "Return" | string;
  reason: string;
  lastStatusDate: string | null;
  clientName: string;
  aging: number;
  operationalStatus?: string;
  source: string;
}

export interface OverallVehicleStatusAnalyticsRow {
  cityName: string;
  vehicleNumber: string;
  ev91RiderId: string;
  riderName: string;
  riderContact: string;
  clientId: string;
  clientName: string;
  vehicleStatus: "Deployed" | "Returned" | string;
  reason: string;
  statusDate: string | null;
  sourceName: string;
  aging: number;
  sdAmount: number;
}

export interface ClientMappingHistoryAnalyticsRow {
  city: string;
  ev91RiderId: string;
  clientId: string;
  clientName: string;
  source: string;
  lastUpdated: string | null;
  phoneNumber: string;
}

export interface APIResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// Vehicle History Service - Extension of vehicleService focused on rider assignments
const vehicleHistoryService = {
  // Get vehicle assignment history for a rider
  async getRiderVehicleHistory(
    riderId: string
  ): Promise<APIResponse<RiderVehicleHistory[]>> {
    try {
      // Use the new endpoint in the rider service
      const response = await riderApi.get(
        `/riders/vehicle-history/riders/${riderId}/vehicle-history`
      );

      // Process the response data to calculate duration days if not provided by API
      const history = response.data.data.map((item: RiderVehicleHistory) => {
        let durationDays: number | undefined = item.durationDays;

        // Calculate duration if not provided using the stored IST-based timestamp string.
        if (durationDays === undefined) {
          const parseStoredTimestamp = (value: string | null | undefined) => {
            if (!value) {
              return null;
            }

            const normalizedValue = String(value).trim().replace(/T/g, " ").replace(/Z$/i, "");
            const [datePart, timePart = "12:00:00"] = normalizedValue.split(" ");
            const [year, month, day] = datePart.split("-").map(Number);
            const [hour = "12", minute = "00", second = "00"] = (timePart.split(".")[0] || "12:00:00").split(":");

            return Date.UTC(
              Number(year),
              Number(month) - 1,
              Number(day),
              Number(hour),
              Number(minute),
              Number(second),
            );
          };

          const startDate = parseStoredTimestamp(item.assignedAt);
          const endDate = parseStoredTimestamp(item.returnedAt) ?? Date.UTC(
            new Date().getUTCFullYear(),
            new Date().getUTCMonth(),
            new Date().getUTCDate(),
            new Date().getUTCHours(),
            new Date().getUTCMinutes(),
            new Date().getUTCSeconds(),
          );

          if (startDate !== null) {
            durationDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
          }
        }

        // Check if we have a stringified JSON in vehicleModel
        if (
          typeof item.vehicleModel === "string" &&
          item.vehicleModel.startsWith("{")
        ) {
          try {
            const vehicleData = JSON.parse(item.vehicleModel);
            // Extract the proper values
            item.vehicleMake =
              vehicleData.oem?.displayName ||
              vehicleData.oem?.name ||
              item.vehicleMake;
            item.vehicleModel = vehicleData.name || vehicleData.modelCode;
            console.log(
              "Parsed JSON from vehicleModel field:",
              item.vehicleMake,
              item.vehicleModel
            );
          } catch (e) {
            console.error("Failed to parse vehicleModel JSON:", e);
          }
        }

        // Create a consistent vehicle object
        const parsedVehicle = {
          id: item.vehicleId,
          registrationNumber: item.registrationNumber,
          make: item.vehicleMake || "Unknown",
          model: item.vehicleModel || "Unknown",
        };

        // Additionally handle case where vehicle field might exist as a JSON string
        if (item.vehicle) {
          try {
            // Only try to parse if it's a string
            const vehicleData =
              typeof item.vehicle === "string" && item.vehicle.startsWith("{")
                ? JSON.parse(item.vehicle)
                : item.vehicle;

            // Use any additional data from vehicle object
            parsedVehicle.id = vehicleData.id || parsedVehicle.id;
            parsedVehicle.registrationNumber =
              vehicleData.registrationNumber ||
              parsedVehicle.registrationNumber;
            parsedVehicle.make =
              vehicleData.oem?.displayName ||
              vehicleData.oem?.name ||
              parsedVehicle.make;
            parsedVehicle.model =
              vehicleData.name || vehicleData.modelCode || parsedVehicle.model;
          } catch (e) {
            console.error("Failed to parse vehicle JSON:", e);
          }
        }

        return {
          ...item,
          durationDays,
          // Create a consistent vehicle object for the frontend
          vehicle: parsedVehicle,
        };
      });

      return {
        success: true,
        data: history,
      };
    } catch (error: any) {
      console.error("Error fetching rider vehicle history:", error);

      // Fallback to mock data if the API fails or is not yet deployed
      if (error.response?.status === 404) {
        console.warn(
          "Vehicle history API not found. Using mock data as fallback."
        );

        // Mock data to simulate vehicle history
        const mockHistory: RiderVehicleHistory[] = [
          {
            id: "vh1-" + riderId.substring(0, 8),
            riderId: riderId,
            vehicleId: "veh-" + Math.random().toString(36).substring(2, 10),
            assignedAt: new Date(
              Date.now() - 60 * 24 * 60 * 60 * 1000
            ).toISOString(), // 60 days ago
            returnedAt: new Date(
              Date.now() - 30 * 24 * 60 * 60 * 1000
            ).toISOString(), // 30 days ago
            assignedBy: "admin-user",
            returnedBy: "admin-user",
            status: "RETURNED",
            notes: "Regular vehicle rotation",
            durationDays: 30,
            registrationNumber: "EV91-001",
            vehicleMake: "Ather",
            vehicleModel: "450X",
            vehicleAgeDays: "60",
          },
          {
            id: "vh2-" + riderId.substring(0, 8),
            riderId: riderId,
            vehicleId: "veh-" + Math.random().toString(36).substring(2, 10),
            assignedAt: new Date(
              Date.now() - 29 * 24 * 60 * 60 * 1000
            ).toISOString(), // 29 days ago
            returnedAt: null,
            assignedBy: "admin-user",
            returnedBy: null,
            status: "ACTIVE",
            notes: "Current vehicle assignment",
            durationDays: 29,
            registrationNumber: "EV91-002",
            vehicleMake: "Ola",
            vehicleModel: "S1 Pro",
            vehicleAgeDays: "29",
          },
        ];

        return {
          success: true,
          data: mockHistory.map((item) => ({
            ...item,
            vehicle: {
              id: item.vehicleId,
              registrationNumber: item.registrationNumber,
              make: item.vehicleMake || "Unknown",
              model: item.vehicleModel || "Unknown",
            },
          })),
        };
      }

      return {
        success: false,
        data: [],
        message: error.message || "Error fetching rider vehicle history",
      };
    }
  }, 

  // Assign vehicle to rider
  async assignVehicleToRider(
    riderId: string,
    vehicleId: string,
    registrationNumber: string,
    notes?: string
  ): Promise<APIResponse<RiderVehicleHistory>> {
    try {
      const response = await riderApi.post(
        `/riders/vehicle-history/riders/${riderId}/vehicle-assignments`,
        {
          vehicleId,
          registrationNumber,
          notes,
        }
      );
      return {
        success: true,
        data: response.data?.data,
      };
    } catch (error: any) {
      console.error("Error assigning vehicle to rider:", error);
      return {
        success: false,
        data: {} as RiderVehicleHistory,
        message: error.message || "Error assigning vehicle to rider",
      };
    }
  },

  // Return vehicle from rider
  async returnVehicleFromRider(
    assignmentId: string,
    notes?: string,
    riderFeedback?: string
  ): Promise<APIResponse<RiderVehicleHistory>> {
    try {
      const response = await riderApi.patch(
        `/riders/vehicle-history/vehicle-assignments/${assignmentId}/return`,
        {
          notes,
          riderFeedback,
        }
      );
      return {
        success: true,
        data: response.data?.data,
      };
    } catch (error: any) {
      console.error("Error returning vehicle from rider:", error);
      return {
        success: false,
        data: {} as RiderVehicleHistory,
        message: error.message || "Error returning vehicle from rider",
      };
    }
  },

  // Get active vehicle assignment for a rider
  async getRiderActiveAssignment(
    riderId: string
  ): Promise<APIResponse<RiderVehicleHistory | null>> {
    try {
      const response = await riderApi.get(
        `/riders/vehicle-history/riders/${riderId}/vehicle-history/active`
      );
      return {
        success: true,
        data: response.data?.data,
      };
    } catch (error: any) {
      console.error("Error fetching rider active assignment:", error);

      // Fallback to checking the Rider entity if the API fails
      try {
        const riderResponse = await riderApi.get(`/riders/${riderId}`);
        const riderData = riderResponse.data?.data;

        if (riderData && riderData.assignedVehicleId) {
          // Use basic rider data to construct a minimal active assignment
          return {
            success: true,
            data: {
              id: `generated-${Date.now()}`,
              riderId: riderId,
              vehicleId: riderData.assignedVehicleId,
              assignedAt: riderData.assignmentDate || new Date().toISOString(),
              returnedAt: null,
              assignedBy: "system",
              returnedBy: null,
              status: "ACTIVE",
              notes: "Assignment data reconstructed from rider record",
              registrationNumber: "Unknown", // We don't have this in the rider data
              vehicleMake: "Unknown",
              vehicleModel: "Unknown",
              vehicleAgeDays: "0",
            },
          };
        }

        // If no assigned vehicle info available on rider record, return null result
        return {
          success: true,
          data: null,
        };
      } catch (fallbackError: any) {
        console.error("Error during fallback check for active assignment:", fallbackError);
        return {
          success: false,
          data: null,
          message: fallbackError.message || "Error fetching active assignment",
        };
      }
    }
  },

  async updateActiveBatteryAndChargerImages(
    riderId: string,
    images: { batteryImage?: File | null; chargerImage?: File | null },
  ): Promise<APIResponse<RiderVehicleHistory | null>> {
    return updateActiveBatteryAndChargerImages(riderId, images);
  },
};

export async function updateActiveBatteryAndChargerImages(
  riderId: string,
  images: { batteryImage?: File | null; chargerImage?: File | null },
): Promise<APIResponse<RiderVehicleHistory | null>> {
  try {
    const formData = new FormData();
    if (images.batteryImage) formData.append("batteryImage", images.batteryImage);
    if (images.chargerImage) formData.append("chargerImage", images.chargerImage);

    const url = `/vehicle-history/riders/${riderId}/vehicle-history/images`;

    const response = await riderApi.patch(url, formData, {
      // Do not set Content-Type here; interceptor will remove it so browser sets the correct boundary
      timeout: 300000, // allow up to 5 minutes for uploads
    } as any);

    const payload = response.data?.data ?? response.data ?? null;

    return {
      success: true,
      data: payload as RiderVehicleHistory | null,
    };
  } catch (error: any) {
    console.error("Error uploading battery/charger images:", error?.response?.data || error.message || error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || "Upload failed",
    };
  }
}

export default vehicleHistoryService;

/**
 * Frontend helper: Fetch all rider vehicle history with pagination
 * Calls: GET /vehicle-history/all (via gateway)
 */
export async function fetchAllRiderVehicleHistory(params?: Record<string, any>) {
  try {
    const response = await riderApi.get(`/vehicle-history/all`, { params });
    console.log('[vehicleHistoryService] fetchAllRiderVehicleHistory response:', {
      status: response.status,
      success: response.data?.success,
      dataLength: response.data?.data?.length || 0,
      pagination: response.data?.pagination,
    });
    return { success: true, data: response.data };
  } catch (error: any) {
    console.error('[vehicleHistoryService] Error fetching all rider vehicle history:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    return {
      success: false,
      data: { data: [], pagination: { total: 0, limit: params?.limit || 20, offset: params?.offset || 0 } },
      message: error.response?.data?.message || error.message || 'Fetch failed',
    };
  }
}

export async function fetchClientNamesFromHistory() {
  try {
    const response = await riderApi.get(`/v1/additional-apis/client-names-from-history`);
    console.log('[vehicleHistoryService] fetchClientNamesFromHistory response:', {
      status: response.status,
      success: response.data?.success,
      total: response.data?.total,
    });

    const clientNames = Array.isArray(response.data?.data)
      ? response.data.data
      : Array.isArray(response.data)
        ? response.data
        : [];

    return {
      success: true,
      data: clientNames,
      total: response.data?.total ?? clientNames.length,
    };
  } catch (error: any) {
    console.error('[vehicleHistoryService] Error fetching client names from rider history:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    return {
      success: false,
      data: [],
      message: error.response?.data?.message || error.message || 'Failed to fetch client names',
    };
  }
}

/**
 * Frontend helper: Export all rider vehicle history as XLSX
 * Calls: GET /vehicle-history/all/export (via gateway)
 */
export async function exportAllRiderVehicleHistory(params?: Record<string, any>) {
  try {
    console.log('[vehicleHistoryService] exportAllRiderVehicleHistory called with params:', params);
    
    const response = await riderApi.get(`/vehicle-history/all/export`, {
      params,
      responseType: 'blob',
    });

    console.log('[vehicleHistoryService] exportAllRiderVehicleHistory response:', {
      status: response.status,
      size: response.data?.size,
      contentType: response.headers?.['content-type'],
    });

    const disposition = response.headers?.['content-disposition'] || '';
    let filename = 'vehicle-history.xlsx';
    const match = disposition.match(/filename="?(.*?)"?(;|$)/i);
    if (match && match[1]) filename = match[1];

    return {
      success: true,
      data: {
        blob: response.data,
        filename,
      },
    };
  } catch (error: any) {
    console.error('[vehicleHistoryService] Error exporting rider vehicle history:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Export failed',
    };
  }
}

export async function fetchCurrentVehicleStatusAnalytics(params?: Record<string, any>) {
  try {
    const response = await riderApi.get(`/riders/vehicle-history/analytics/current-status`, { params });
    return { success: true, data: response.data };
  } catch (error: any) {
    console.error('[vehicleHistoryService] Error fetching current vehicle status analytics:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    return {
      success: false,
      data: {
        data: [],
        pagination: { total: 0, limit: params?.limit || 25, offset: params?.offset || 0 },
        filters: { cities: [] },
        summary: { total: 0, deployed: 0, returned: 0, yetNotDeployed: 0 },
      },
      message: error.response?.data?.message || error.message || 'Fetch failed',
    };
  }
}

export async function exportCurrentVehicleStatusAnalytics(params?: Record<string, any>) {
  try {
    const response = await riderApi.get(`/riders/vehicle-history/analytics/current-status/export`, {
      params,
      responseType: 'blob',
    });

    const disposition = response.headers?.['content-disposition'] || '';
    let filename = 'current-vehicle-status.xlsx';
    const match = disposition.match(/filename="?(.*?)"?(;|$)/i);
    if (match && match[1]) filename = match[1];

    return {
      success: true,
      data: {
        blob: response.data,
        filename,
      },
    };
  } catch (error: any) {
    console.error('[vehicleHistoryService] Error exporting current vehicle status analytics:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Export failed',
    };
  }
}

export async function fetchOverallVehicleStatusAnalytics(params?: Record<string, any>) {
  try {
    const response = await riderApi.get(`/riders/vehicle-history/analytics/overall-status`, { params });
    return { success: true, data: response.data };
  } catch (error: any) {
    console.error('[vehicleHistoryService] Error fetching overall vehicle status analytics:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    return {
      success: false,
      data: {
        data: [],
        pagination: { total: 0, limit: params?.limit || 25, offset: params?.offset || 0 },
        filters: { cities: [] },
        summary: { total: 0, deployed: 0, returned: 0, yetNotDeployed: 0 },
      },
      message: error.response?.data?.message || error.message || 'Fetch failed',
    };
  }
}

export async function exportOverallVehicleStatusAnalytics(params?: Record<string, any>) {
  try {
    const response = await riderApi.get(`/riders/vehicle-history/analytics/overall-status/export`, {
      params,
      responseType: 'blob',
    });

    const disposition = response.headers?.['content-disposition'] || '';
    let filename = 'overall-vehicle-status.xlsx';
    const match = disposition.match(/filename="?(.*?)"?(;|$)/i);
    if (match && match[1]) filename = match[1];

    return {
      success: true,
      data: {
        blob: response.data,
        filename,
      },
    };
  } catch (error: any) {
    console.error('[vehicleHistoryService] Error exporting overall vehicle status analytics:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Export failed',
    };
  }
}

export async function fetchClientMappingHistoryAnalytics(params?: Record<string, any>) {
  try {
    const response = await riderApi.get(`/riders/vehicle-history/analytics/client-mapping-history`, { params });
    return { success: true, data: response.data };
  } catch (error: any) {
    console.error('[vehicleHistoryService] Error fetching client mapping history analytics:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    return {
      success: false,
      data: {
        data: [],
        pagination: { total: 0, limit: params?.limit || 25, offset: params?.offset || 0 },
        filters: { cities: [] },
        summary: { total: 0 },
      },
      message: error.response?.data?.message || error.message || 'Fetch failed',
    };
  }
}

export async function exportClientMappingHistoryAnalytics(params?: Record<string, any>) {
  try {
    const response = await riderApi.get(`/riders/vehicle-history/analytics/client-mapping-history/export`, {
      params,
      responseType: 'blob',
    });

    const disposition = response.headers?.['content-disposition'] || '';
    let filename = 'client-mapping-history.xlsx';
    const match = disposition.match(/filename="?(.*?)"?(;|$)/i);
    if (match && match[1]) filename = match[1];

    return {
      success: true,
      data: {
        blob: response.data,
        filename,
      },
    };
  } catch (error: any) {
    console.error('[vehicleHistoryService] Error exporting client mapping history analytics:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Export failed',
    };
  }
}

/**
 * Frontend helper: Calculate vehicle rent from Excel file
 * Calls: POST /api/rent/calculate-upload (via gateway)
 */
export async function calculateVehicleRent(
  file: File,
  weekStartDate: string,
  weekEndDate: string
) {
  try {
    console.log('[vehicleHistoryService] calculateVehicleRent called with:', {
      fileName: file.name,
      weekStartDate,
      weekEndDate,
    });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('weekStartDate', weekStartDate);
    formData.append('weekEndDate', weekEndDate);

    const response = await axios.post(`${API_GATEWAY_BASE}/rent/calculate-upload`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        // Content-Type will be set automatically to multipart/form-data
      },
      timeout: 60000, // 1 minute timeout for file processing
      responseType: 'blob', // Expect blob/file response
    });

    console.log('[vehicleHistoryService] calculateVehicleRent response:', {
      status: response.status,
      contentType: response.headers?.['content-type'],
      size: response.data?.size,
    });

    return {
      success: true,
      data: {
        blob: response.data,
        filename: `vehicle-rent-calculation-${new Date().toISOString().replace(/[:.]/g, '-')}.xlsx`,
      },
      message: 'Rent calculation completed successfully',
    };
  } catch (error: any) {
    console.error('[vehicleHistoryService] Error calculating vehicle rent:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || error.message || 'Rent calculation failed',
    };
  }
}

export interface VehicleRentUpdateResult {
  batchId: string;
  totalRentAmount: number;
  updatedRiders: number;
  updatedRentKeys: number;
  sourceRows: number;
  totalBillableDays: number;
}

export interface VehicleRentUpdateError {
  message: string;
  rowErrors?: Array<{ row: number; message: string }>;
  conflicts?: Array<{
    RiderPublicID: string;
    ClientRiderId: string;
    weekStartDate: string;
    weekEndDate: string;
    existingWeekStartDate?: string;
    existingWeekEndDate?: string;
    rows: number[];
    existingRows?: number[];
    source?: 'previous-update' | 'same-upload';
  }>;
  unknownRiderPublicIds?: string[];
}

/**
 * Upload a completed rent calculation workbook to update rider deficit rent.
 */
export async function updateVehicleRentAmount(
  file: File,
): Promise<
  | { success: true; data: VehicleRentUpdateResult; message: string }
  | { success: false; data: null; error: VehicleRentUpdateError }
> {
  try {
    const storedToken = localStorage.getItem('authToken')?.trim();
    if (!storedToken) {
      return {
        success: false,
        data: null,
        error: {
          message: 'Your login session is missing. Sign in again, then retry the rent update.',
        },
      };
    }

    const token = storedToken.replace(/^Bearer\s+/i, '');
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${API_GATEWAY_BASE}/rent/update-rent-amount`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          // Let the browser set the multipart boundary.
        },
        timeout: 120000,
      },
    );

    return {
      success: true,
      data: response.data.data as VehicleRentUpdateResult,
      message: response.data.message || 'Rent amount updated successfully',
    };
  } catch (error: any) {
    const responseData = error.response?.data;
    const responseMessage =
      typeof responseData?.message === 'string' ? responseData.message : '';
    const message = error.response?.status === 401
      ? `${responseMessage || 'Authorization failed.'} Sign in again and retry the rent update.`
      : responseMessage || error.message || 'Rent amount update failed';

    console.error('[vehicleHistoryService] Error uploading rent amount file:', {
      status: error.response?.status,
      message,
      data: responseData,
    });

    return {
      success: false,
      data: null,
      error: {
        message,
        rowErrors: Array.isArray(responseData?.rowErrors)
          ? responseData.rowErrors
          : undefined,
        conflicts: Array.isArray(responseData?.conflicts)
          ? responseData.conflicts
          : undefined,
        unknownRiderPublicIds: Array.isArray(responseData?.unknownRiderPublicIds)
          ? responseData.unknownRiderPublicIds
          : undefined,
      },
    };
  }
}

export interface VehicleRentUpdateItem {
  id: string;
  batchId: string;
  publicRiderId: string;
  riderName: string | null;
  clientRiderId: string | null;
  clientName: string | null;
  registrationNumber: string | null;
  weekStartDate: string | null;
  weekEndDate: string | null;
  numberOfDays: number;
  totalRent: number;
  assignmentCount: number;
  beforeRentAmount: number;
  afterRentAmount: number;
  updatedBy: string | null;
  createdAt: string;
  batchCreatedAt: string;
}

export interface VehicleRentUpdateItemsFilters {
  page: number;
  pageSize: number;
  search?: string;
  clientName?: string;
  weekStartDateFrom?: string;
  weekStartDateTo?: string;
}

export interface VehicleRentUpdateItemsResponse {
  data: VehicleRentUpdateItem[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface VehicleRentUpdateBatch {
  id: string;
  weekStartDate: string;
  weekEndDate: string;
  updatedBy: string | null;
  sourceFileUrl: string | null;
  createdAt: string;
}

export interface VehicleRentUpdateBatchesResponse {
  data: VehicleRentUpdateBatch[];
  pagination: VehicleRentUpdateItemsResponse["pagination"];
}

export async function fetchVehicleRentUpdateItems(
  filters: VehicleRentUpdateItemsFilters,
): Promise<VehicleRentUpdateItemsResponse> {
  const storedToken = localStorage.getItem("authToken")?.trim();
  if (!storedToken) {
    throw new Error("Your login session is missing. Sign in again to view rent updates.");
  }

  const token = storedToken.replace(/^Bearer\s+/i, "");
  try {
    const response = await axios.get(`${API_GATEWAY_BASE}/rent/update-items`, {
      params: filters,
      headers: { Authorization: `Bearer ${token}` },
      timeout: 30000,
    });

    if (
      response.data?.success !== true ||
      !Array.isArray(response.data.data) ||
      !response.data.pagination
    ) {
      throw new Error(response.data?.message || "The rent update response was invalid.");
    }

    return {
      data: response.data.data as VehicleRentUpdateItem[],
      pagination: response.data.pagination,
    };
  } catch (error: any) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Unable to load vehicle rent update items.";
    console.error("[vehicleHistoryService] Error fetching rent update items:", {
      status: error.response?.status,
      message,
      data: error.response?.data,
    });
    throw new Error(message);
  }
}

export async function fetchVehicleRentUpdateBatches(
  filters: Pick<
    VehicleRentUpdateItemsFilters,
    "page" | "pageSize" | "weekStartDateFrom" | "weekStartDateTo"
  >,
): Promise<VehicleRentUpdateBatchesResponse> {
  const storedToken = localStorage.getItem("authToken")?.trim();
  if (!storedToken) {
    throw new Error("Your login session is missing. Sign in again to view rent update batches.");
  }

  const token = storedToken.replace(/^Bearer\s+/i, "");
  try {
    const response = await axios.get(`${API_GATEWAY_BASE}/rent/update-batches`, {
      params: filters,
      headers: { Authorization: `Bearer ${token}` },
      timeout: 30000,
    });

    if (
      response.data?.success !== true ||
      !Array.isArray(response.data.data) ||
      !response.data.pagination
    ) {
      throw new Error(response.data?.message || "The rent update batches response was invalid.");
    }

    return {
      data: response.data.data as VehicleRentUpdateBatch[],
      pagination: response.data.pagination,
    };
  } catch (error: any) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Unable to load vehicle rent update batches.";
    console.error("[vehicleHistoryService] Error fetching rent update batches:", {
      status: error.response?.status,
      message,
      data: error.response?.data,
    });
    throw new Error(message);
  }
}
